import { useEffect, useMemo } from "react";

import { Alert, Link } from "@mui/material";
import { signInWithRedirect } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@covertree/hooks";

import { BASE_AUTH_PATH } from "../paths";

interface FederatedSignInProps {
  allowedOauthProviders: string[];
}

export default function FederatedSignIn({
  allowedOauthProviders,
}: FederatedSignInProps) {
  const query = useQuery();
  const navigate = useNavigate();

  const errorMessage = useMemo(() => {
    const errorMessageFromSAML = query.get("error_description");

    if (!errorMessageFromSAML) {
      return undefined;
    }

    const formattedErrorMessage = errorMessageFromSAML
      .trim()
      .replace("Invalid SAML response received: ", "")
      .replace("PostConfirmation failed with error ", "")
      .slice(0, -1);

    if (formattedErrorMessage.includes("custom=accountNotReady")) {
      // Replace + with %2B before parsing to prevent URLSearchParams from converting '+' to space
      const escapedErrorMessage = formattedErrorMessage.replace(/\+/g, "%2B");
      const urlParser = new URLSearchParams(escapedErrorMessage);
      const wholesalerPartnerId = encodeURIComponent(
        urlParser.get("wholesalerPartnerId") || ""
      );
      const email = encodeURIComponent(urlParser.get("email") || "");

      const onboardingUrl = `https://${process.env.VITE_ENV_NAME === "prod" ? "sign-up.covertree.com" : "sign-up.staging.covertree.com"}?email=${email}&wholesalerPartnerId=${wholesalerPartnerId}`;

      window.location.replace(onboardingUrl);

      return "redirect";
    }

    return formattedErrorMessage;
  }, [query]);

  useEffect(() => {
    const custom = query.get("provider");

    if (!errorMessage && (!custom || !allowedOauthProviders.includes(custom))) {
      navigate(`/${BASE_AUTH_PATH}`);
    }
  }, []);

  if (errorMessage === "redirect") {
    return "Redirecting to Onboarding Form...";
  }

  if (errorMessage) {
    return (
      <Alert severity="error">
        {errorMessage}
        <br />
        If you have any questions please email us at{" "}
        <Link href="mailto:agentsales@covertree.com">
          agentsales@covertree.com
        </Link>
        .
      </Alert>
    );
  }

  const custom = query.get("provider");

  if (!custom || !allowedOauthProviders.includes(custom)) {
    return "Redirecting to Login Page...";
  }

  const code = query.get("code");
  const state = query.get("state");

  if (code && state) {
    return "Sign in successful! Redirecting to Overview...";
  }

  signInWithRedirect({ provider: { custom } });

  return "Redirecting to SSO provider...";
}
